import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../services/api';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

interface CampingPuzzle {
  trees: string[][];
  solution: string[][];
  rowCounts: number[];
  colCounts: number[];
  seed: number;
  size: number;
}

function verifyBijection(board: string[][], sol: string[][], size: number): boolean {
  const tents: [number, number][] = [];
  const trees: [number, number][] = [];
  for (let r = 0; r < size; r++)
    for (let c = 0; c < size; c++) {
      if (board[r][c] === '⛺') tents.push([r, c]);
      if (sol[r][c] === '🌲') trees.push([r, c]);
    }

  if (tents.length !== trees.length) return false;

  const adj: number[][] = trees.map(([tr, tc]) =>
    tents.reduce<number[]>((acc, [tr2, tc2], idx) => {
      if (Math.abs(tr - tr2) + Math.abs(tc - tc2) === 1) acc.push(idx);
      return acc;
    }, [])
  );

  const treeAssign = Array(trees.length).fill(-1);

  const tryMatch = (tentIdx: number, visited: boolean[]): boolean => {
    for (let ti = 0; ti < trees.length; ti++) {
      if (adj[ti].includes(tentIdx) && !visited[ti]) {
        visited[ti] = true;
        if (treeAssign[ti] === -1 || tryMatch(treeAssign[ti], visited)) {
          treeAssign[ti] = tentIdx;
          return true;
        }
      }
    }
    return false;
  };

  let matched = 0;
  for (let tentIdx = 0; tentIdx < tents.length; tentIdx++) {
    if (tryMatch(tentIdx, Array(trees.length).fill(false))) matched++;
  }

  return matched === tents.length;
}

export default function CampingGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();

  const [puzzle, setPuzzle] = useState<CampingPuzzle | null>(null);
  const [userBoard, setUserBoard] = useState<string[][]>([]);
  const [history, setHistory] = useState<string[][][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [startTime] = useState(Date.now());
  const [isCompleted, setIsCompleted] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modoAsistido, setModoAsistido] = useState(false);

  const dificultadOrden = parseInt(dificultad || '1');
  const isFirstLoad = useRef(true);
  const [forceReload, setForceReload] = useState(0);
  const { saveProgress: saveGameProgress } = useGameProgress('camping', dificultadOrden);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  const difficulties = [
    { orden: 1, nombre: 'Neurona', icono: '🧠' },
    { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
    { orden: 3, nombre: 'Cerebro', icono: '🧩' },
    { orden: 4, nombre: 'Genio', icono: '💡' },
    { orden: 5, nombre: 'Einstein', icono: '🎓' },
  ];

  // Mapeo de dificultad a tamaño
  const getSizeFromDificultad = (orden: number): number => {
    const sizeMap: Record<number, number> = {
      1: 6,  // Neurona
      2: 7,  // Sinapsis
      3: 8,  // Cerebro
      4: 9,  // Genio
      5: 10, // Einstein
    };
    return sizeMap[orden] || 6;
  };

  // Cargar puzzle
  useEffect(() => {
    const loadPuzzle = async () => {
      try {
        setLoading(true);
        setError('');

        const size = getSizeFromDificultad(dificultadOrden);
        const response = await api.get('/camping/generate', {
          params: { size }
        });

        const puzzleData: CampingPuzzle = response.data.data;
        setPuzzle(puzzleData);

        // Inicializar tablero vacío (solo árboles visibles)
        const initialBoard = puzzleData.trees.map(row => [...row]);
        setUserBoard(initialBoard);
        setHistory([JSON.parse(JSON.stringify(initialBoard))]);
        setHistoryIndex(0);
        setIsCompleted(false);
        setShowVictory(false);
      } catch (err: any) {
        console.error('Error al cargar puzzle:', err);
        setError(err.response?.data?.error || 'Error al cargar el puzzle');
      } finally {
        setLoading(false);
        isFirstLoad.current = false;
      }
    };

    loadPuzzle();
  }, [dificultadOrden, forceReload]);

  // Guardar estado en historial
  const saveState = (newState: string[][]) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(newState)));

    if (newHistory.length > 50) {
      newHistory.shift();
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    } else {
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  };

  // Cicla el estado de la celda: vacío → ⛺ → 🟩 → vacío
  const handleCellClick = (r: number, c: number) => {
    if (isCompleted || !puzzle) return;
    if (puzzle.trees[r][c] === '🌲') return;

    const newBoard = userBoard.map(row => [...row]);
    const current = newBoard[r][c];

    if (current === '') {
      newBoard[r][c] = '⛺';
    } else if (current === '⛺') {
      newBoard[r][c] = '🟩';
    } else {
      newBoard[r][c] = '';
    }

    setUserBoard(newBoard);
    saveState(newBoard);
    checkVictory(newBoard, false);
  };

  // Deshacer
  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setUserBoard(JSON.parse(JSON.stringify(history[historyIndex - 1])));
    }
  };

  // Rehacer
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setUserBoard(JSON.parse(JSON.stringify(history[historyIndex + 1])));
    }
  };

  // Pista: revela una carpa incorrecta o una celda vacía que debería ser carpa
  const handleHint = () => {
    if (!puzzle || isCompleted) return;

    // Primero corregir una carpa en posición incorrecta
    for (let r = 0; r < puzzle.size; r++) {
      for (let c = 0; c < puzzle.size; c++) {
        if (userBoard[r][c] === '⛺' && puzzle.solution[r][c] !== '⛺') {
          const next = userBoard.map(row => [...row]);
          next[r][c] = puzzle.solution[r][c];
          setUserBoard(next);
          saveState(next);
          return;
        }
      }
    }

    // Luego revelar una celda vacía que debería ser carpa
    const candidates: [number, number][] = [];
    for (let r = 0; r < puzzle.size; r++) {
      for (let c = 0; c < puzzle.size; c++) {
        if (userBoard[r][c] !== '⛺' && puzzle.solution[r][c] === '⛺') {
          candidates.push([r, c]);
        }
      }
    }
    if (candidates.length === 0) {
      toast.info('No hay más pistas disponibles');
      return;
    }
    const [r, c] = candidates[Math.floor(Math.random() * candidates.length)];
    const next = userBoard.map(row => [...row]);
    next[r][c] = '⛺';
    setUserBoard(next);
    saveState(next);
  };

  // Ver solución completa
  const handleShowSolution = () => {
    if (!puzzle || !window.confirm('¿Estás seguro de que quieres ver la solución? Esto no guardará tu progreso.')) return;
    const sol = puzzle.solution.map(row => [...row]);
    setUserBoard(sol);
    saveState(sol);
    toast.info('Solución revelada. No se guardará este progreso.');
  };

  // Reiniciar
  const handleReset = () => {
    if (!puzzle || !window.confirm('¿Estás seguro de que quieres reiniciar el tablero?')) return;

    const resetBoard = puzzle.trees.map(row => [...row]);
    setUserBoard(resetBoard);
    setHistory([JSON.parse(JSON.stringify(resetBoard))]);
    setHistoryIndex(0);
    setIsCompleted(false);
    setShowVictory(false);
    setError('');
  };

  // Verificar victoria
  const checkVictory = (board: string[][], manual: boolean) => {
    if (!puzzle) return;

    let tentCount = 0;
    for (let r = 0; r < puzzle.size; r++) {
      for (let c = 0; c < puzzle.size; c++) {
        if (board[r][c] === '⛺') tentCount++;
      }
    }

    const totalNeeded = puzzle.rowCounts.reduce((a, b) => a + b, 0);

    if (tentCount !== totalNeeded) {
      if (manual) setError('Aún faltan carpas por colocar');
      return;
    }

    let valid = true;

    for (let r = 0; r < puzzle.size; r++) {
      let count = 0;
      for (let c = 0; c < puzzle.size; c++) {
        if (board[r][c] === '⛺') count++;
      }
      if (count !== puzzle.rowCounts[r]) {
        valid = false;
        break;
      }
    }

    if (valid) {
      for (let c = 0; c < puzzle.size; c++) {
        let count = 0;
        for (let r = 0; r < puzzle.size; r++) {
          if (board[r][c] === '⛺') count++;
        }
        if (count !== puzzle.colCounts[c]) {
          valid = false;
          break;
        }
      }
    }

    if (valid) {
      for (let r = 0; r < puzzle.size; r++) {
        for (let c = 0; c < puzzle.size; c++) {
          if (board[r][c] === '⛺') {
            for (let dr = -1; dr <= 1; dr++) {
              for (let dc = -1; dc <= 1; dc++) {
                if (dr === 0 && dc === 0) continue;
                const nr = r + dr;
                const nc = c + dc;
                if (nr >= 0 && nr < puzzle.size && nc >= 0 && nc < puzzle.size) {
                  if (board[nr][nc] === '⛺') {
                    valid = false;
                    break;
                  }
                }
              }
              if (!valid) break;
            }

            if (valid) {
              let hasTree = false;
              const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]];
              for (const [dr, dc] of directions) {
                const nr = r + dr;
                const nc = c + dc;
                if (nr >= 0 && nr < puzzle.size && nc >= 0 && nc < puzzle.size) {
                  if (puzzle.solution[nr][nc] === '🌲') {
                    hasTree = true;
                    break;
                  }
                }
              }
              if (!hasTree) valid = false;
            }

            if (!valid) break;
          }
        }
        if (!valid) break;
      }
    }

    if (valid) {
      valid = verifyBijection(board, puzzle.solution, puzzle.size);
    }

    if (valid && !isCompleted) {
      setIsCompleted(true);
      const tiempo = Math.floor((Date.now() - startTime) / 1000);
      saveProgress(tiempo);
      setShowVictory(true);
    } else if (manual && !valid) {
      setError('La solución no es correcta. Revisa las reglas.');
    }
  };

  // Guardar progreso
  const saveProgress = async (tiempo: number) => {
    if (!puzzle) return;

    try {
      await saveGameProgress({
        tablero_id: puzzle.seed,
        tiempo,
        movimientos: 0,
        completado: true,
      });

      await saveSessionResult({
        tiempo,
        movimientos: 0,
        completado: true,
        datos_juego: {
          tablero_id: puzzle.seed,
          size: puzzle.size,
        },
      });
    } catch (error) {
      console.error('Error al guardar progreso:', error);
    }
  };

  // ─── Modo asistido ────────────────────────────────────────────────────────
  function calcularErrores(board: string[][], puz: CampingPuzzle): {
    cells: Set<string>; rows: Set<number>; cols: Set<number>;
  } {
    const cells = new Set<string>();
    const rows  = new Set<number>();
    const cols  = new Set<number>();
    const size  = puz.size;

    // 1. Carpas adyacentes (8 direcciones)
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (board[r][c] !== '⛺') continue;
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            if (dr === 0 && dc === 0) continue;
            const nr = r+dr, nc = c+dc;
            if (nr>=0 && nr<size && nc>=0 && nc<size && board[nr][nc] === '⛺') {
              cells.add(`${r},${c}`);
            }
          }
        }

        // 2. Carpa sin árbol ortogonal adyacente
        const dirs = [[-1,0],[1,0],[0,-1],[0,1]];
        const hasTree = dirs.some(([dr,dc]) => {
          const nr=r+dr, nc=c+dc;
          return nr>=0 && nr<size && nc>=0 && nc<size && puz.trees[nr][nc] === '🌲';
        });
        if (!hasTree) cells.add(`${r},${c}`);
      }
    }

    // 3. Contadores de fila
    for (let r = 0; r < size; r++) {
      let tents = 0, empty = 0;
      for (let c = 0; c < size; c++) {
        if (board[r][c] === '⛺') tents++;
        else if (board[r][c] === '') empty++;
      }
      if (tents > puz.rowCounts[r] || tents + empty < puz.rowCounts[r]) rows.add(r);
    }

    // 4. Contadores de columna
    for (let c = 0; c < size; c++) {
      let tents = 0, empty = 0;
      for (let r = 0; r < size; r++) {
        if (board[r][c] === '⛺') tents++;
        else if (board[r][c] === '') empty++;
      }
      if (tents > puz.colCounts[c] || tents + empty < puz.colCounts[c]) cols.add(c);
    }

    return { cells, rows, cols };
  }

  // Tamaño de celda: llena el espacio disponible manteniendo celdas cuadradas
  // +1 por las cabeceras de fila/columna
  const n = puzzle?.size ?? 6;
  const fontSize = `calc(min(100vw - 8px, 100vh - 110px) / ${n + 1} * 0.55)`;
  const fontSizeSmaller = `calc(min(100vw - 8px, 100vh - 110px) / ${n + 1} * 0.42)`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-white text-2xl">🔄 Cargando...</div>
      </div>
    );
  }

  if (!puzzle) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-white text-2xl">❌ Error al cargar puzzle</div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden bg-gradient-to-br from-purple-600 to-blue-600 flex flex-col">
      {/* Banner de Sesión */}
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Barra de controles compacta */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        {/* Título */}
        <span className="text-white font-bold text-base whitespace-nowrap">🏕️ Camping</span>

        <div className="w-px h-5 bg-white/30 hidden sm:block" />

        {/* Selector de dificultad */}
        <div className="flex gap-1 flex-wrap">
          {difficulties.map((d) => (
            <button
              key={d.orden}
              onClick={() => {
                if (dificultadOrden === d.orden) setForceReload(prev => prev + 1);
                else navigate(`/juegos/camping/${d.orden}`);
              }}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition-all ${
                dificultadOrden === d.orden
                  ? 'bg-white text-purple-700 shadow'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              title={d.nombre}
            >
              {d.icono} <span className="hidden sm:inline">{d.nombre}</span>
            </button>
          ))}
        </div>

        <div className="flex-1" />

        {/* Acciones */}
        <div className="flex gap-1 items-center">
          <button
            onClick={handleUndo}
            disabled={historyIndex <= 0 || isCompleted}
            className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            title="Deshacer"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 7v6h6"/><path d="M3 13C5 8 9.5 5 15 5c5 0 8 3 8 7s-3 7-8 7H9"/></svg>
          </button>
          <button
            onClick={handleRedo}
            disabled={historyIndex >= history.length - 1 || isCompleted}
            className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            title="Rehacer"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 7v6h-6"/><path d="M21 13C19 8 14.5 5 9 5c-5 0-8 3-8 7s3 7 8 7h6"/></svg>
          </button>
          <button
            onClick={() => setModoAsistido(v => !v)}
            className={`p-2 rounded-lg transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          <button
            onClick={handleHint}
            disabled={isCompleted}
            className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            title="Pista"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m1.343-5.657l-.707-.707M12 21v-1M5.636 5.636l.707.707M12 13a3 3 0 100-6 3 3 0 000 6z" /></svg>
          </button>
          <button
            onClick={handleShowSolution}
            disabled={isCompleted}
            className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            title="Ver solución"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
          </button>
          <button
            onClick={handleReset}
            disabled={isCompleted}
            className="p-2 rounded-lg bg-red-500/70 text-white hover:bg-red-500 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            title="Reiniciar tablero"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
          </button>
          <button
            onClick={() => setForceReload(prev => prev + 1)}
            className="p-2 rounded-lg bg-green-500/70 text-white hover:bg-green-500 transition-all"
            title="Nuevo tablero"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
          </button>
          <button
            onClick={() => setShowHowToPlay(true)}
            className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 transition-all"
            title="Cómo se juega"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
          </button>
        </div>
      </div>

      {/* Mensaje de error */}
      {error && (
        <div className="mx-3 mt-2 p-2 bg-blue-100 border border-blue-300 text-blue-800 rounded-lg text-sm text-center">
          {error}
        </div>
      )}

      {/* Tablero - ocupa todo el espacio restante */}
      <div className="flex-1 min-h-0 p-1 flex flex-col">
        {(() => {
          const errores = modoAsistido ? calcularErrores(userBoard, puzzle) : null;
          return (<>
            {/* Fila superior: vacío + conteos de columnas */}
            <div className="flex flex-1">
              <div className="flex-1" />
              {puzzle.colCounts.map((count, c) => (
                <div
                  key={`col-${c}`}
                  className="flex-1 flex items-center justify-center font-bold"
                  style={{
                    fontSize: fontSizeSmaller,
                    color: errores?.cols.has(c) ? '#ef4444' : undefined,
                    textShadow: errores?.cols.has(c) ? '0 0 8px #ef4444' : undefined,
                  }}
                >
                  {count}
                </div>
              ))}
            </div>

            {/* Filas del tablero */}
            {Array.from({ length: puzzle.size }, (_, r) => (
              <div key={`row-${r}`} className="flex flex-1">
                {/* Conteo de fila */}
                <div
                  className="flex-1 flex items-center justify-center font-bold"
                  style={{
                    fontSize: fontSizeSmaller,
                    color: errores?.rows.has(r) ? '#ef4444' : undefined,
                    textShadow: errores?.rows.has(r) ? '0 0 8px #ef4444' : undefined,
                  }}
                >
                  {puzzle.rowCounts[r]}
                </div>

                {/* Celdas */}
                {Array.from({ length: puzzle.size }, (_, c) => {
                  const isTree = puzzle.trees[r][c] === '🌲';
                  const cellContent = userBoard[r][c];
                  const hasError = errores?.cells.has(`${r},${c}`) ?? false;

                  let bgColor: string;
                  let border: string | undefined;
                  let cursor: string;
                  if (isTree) {
                    bgColor = '#fde68a'; border = '2px solid #b45309'; cursor = 'default';
                  } else if (hasError) {
                    bgColor = '#fca5a5'; border = '2px solid #ef4444'; cursor = 'pointer';
                  } else if (cellContent === '⛺') {
                    bgColor = '#bbf7d0'; border = '2px solid #86efac'; cursor = 'pointer';
                  } else if (cellContent === '🟩') {
                    bgColor = '#dcfce7'; border = '2px solid #bbf7d0'; cursor = 'pointer';
                  } else {
                    bgColor = 'rgba(255,255,255,0.8)'; border = undefined; cursor = 'pointer';
                  }
                  return (
                    <div
                      key={`${r}-${c}`}
                      onClick={() => !isTree && handleCellClick(r, c)}
                      onContextMenu={(e) => e.preventDefault()}
                      className="flex-1 flex items-center justify-center rounded-lg transition-all m-0.5"
                      style={{ fontSize, backgroundColor: bgColor, border, cursor }}
                    >
                      {cellContent || ''}
                    </div>
                  );
                })}
              </div>
            ))}
          </>);
        })()}
      </div>

      {/* Modal Cómo se juega */}
      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Camping"
        gameIcon="🏕️"
        objective="Coloca todas las carpas ⛺ en el tablero siguiendo las reglas del camping. Cada árbol 🌲 debe tener exactamente una carpa adyacente."
        rules={[
          'Cada carpa debe estar junto a un árbol (horizontal o vertical, NO en diagonal)',
          'Las carpas NO pueden tocarse entre sí (ni horizontal, ni vertical, ni diagonal)',
          'Los números en los bordes indican cuántas carpas hay en esa fila o columna',
          'Cada árbol tiene exactamente una carpa asociada',
        ]}
        howToPlay="Toca/click para ciclar el estado de cada celda: vacío → carpa ⛺ → césped 🟩 → vacío."
        author="Simon Tatham"
      />

      {/* Modal de Victoria */}
      <VictoryModal
        isOpen={showVictory}
        onClose={() => setShowVictory(false)}
        stats={{
          tiempo: Math.floor((Date.now() - startTime) / 1000),
          movimientos: 0,
        }}
      />
    </div>
  );
}
